import * as React from "react";
import { ResponseProductsSearch } from "../../model";
import { PlpState_UrlDataReadyToCache } from "../../url-state";

import { usePlpAnalytics } from "../plp-analytics-provider";
import { usePlpDataSourceProductListQuery } from "../plp-data-source-product-list-provider";
import { useReactPlpInitialState } from "../react-plp-intial-state-provider";

import { useProductListState, useProductListState_asController } from "./product-list-state-provider";
import { useProductListUrlStateController } from "./product-list-url-state-controller-provider";

interface ProductListPaginationConsumerInterface {
  hasPreviousPages: boolean;
  hasMorePages: boolean;
}

interface ProductListPaginationConsumer_ControllerInterface {
  loadMore: () => void;
  loadPrevious: () => void;
  resetLoadMorePagination: () => void;
}

const defaultValue = {} as unknown as ProductListPaginationConsumerInterface;
const ProductListPaginationContext = React.createContext(defaultValue);

const defaultControllerValue =
  {} as unknown as ProductListPaginationConsumer_ControllerInterface;
const ProductListPaginationControllerContext = React.createContext(
  defaultControllerValue
);

export const ProductListPaginationProvider: React.FunctionComponent = (
  props
) => {
  const { hasNextPage, hasPreviousPage, fetchNextPage, fetchPreviousPage } =
    usePlpDataSourceProductListQuery();

  const { startPage } = useProductListState();
  
  const { handleNewNextPageInProductListState } =
    useProductListUrlStateController();

  const { analytics } = usePlpAnalytics();

  const { initialState } = useReactPlpInitialState();
  const { baseQuery } = initialState || {};
  const { search } = baseQuery || {};

  const { setStartPage } = useProductListState_asController();

  const [loadMorePageOffset, setLoadMorePageOffset] = React.useState(0);

  const resetLoadMorePagination = React.useCallback(() => {
    setLoadMorePageOffset(0);
  }, []);
  const loadMore = React.useCallback(async () => {
    try {
      const result = await fetchNextPage();

      const fredhopperResponse =
        result.data?.pages[result.data?.pages.length - 1];

      if (fredhopperResponse) {
        const thePageIsNowOnTheNextPage = loadMorePageOffset + 1;
        setLoadMorePageOffset(thePageIsNowOnTheNextPage);
        handleNewNextPageInProductListState(
          startPage + thePageIsNowOnTheNextPage
        );

        analytics.onViewFredhopperListingPage({
          fredhopperResponse,
          initialStateSearchTerms: search,
        });
      }
    } catch (error) {
      console.error(error);
    }
  }, [
    fetchNextPage,
    analytics,
    search,
    handleNewNextPageInProductListState,
    loadMorePageOffset,
    startPage,
  ]);

  const loadPrevious = React.useCallback(async () => {
    try {
      const result = await fetchPreviousPage();

      setStartPage((startPage) => Math.max(startPage - 1, 0));

      const fredhopperResponse = result.data?.pages[0];
      if (fredhopperResponse) {
        analytics.onViewFredhopperListingPage({
          fredhopperResponse,
          initialStateSearchTerms: search,
        });
      }
    } catch (error) {
      console.error(error);
    }
  }, [fetchPreviousPage, analytics, search]);

  const value: ProductListPaginationConsumerInterface = React.useMemo(
    () => ({
      hasMorePages: !!hasNextPage,
      hasPreviousPages: !!hasPreviousPage,
    }),
    [hasNextPage, hasPreviousPage]
  );
  const controllerValue: ProductListPaginationConsumer_ControllerInterface =
    React.useMemo(
      () => ({
        loadMore,
        loadPrevious,
        resetLoadMorePagination,
      }),
      [loadMore, loadPrevious, resetLoadMorePagination]
    );
  return (
    <ProductListPaginationContext.Provider value={value}>
      <ProductListPaginationControllerContext.Provider value={controllerValue}>
        {props.children || []}
      </ProductListPaginationControllerContext.Provider>
    </ProductListPaginationContext.Provider>
  );
};

export const useProductListPagination = () => {
  const value = React.useContext(ProductListPaginationContext);
  if (value === defaultValue) {
    throw new Error(
      "useProductListPagination must be used within a ProductListPaginationProvider"
    );
  }
  return value;
};

export const useProductListPagination_asController = () => {
  const controllerValue = React.useContext(
    ProductListPaginationControllerContext
  );
  if (controllerValue === defaultControllerValue) {
    throw new Error(
      "useProductListPagination_asController must be used within a ProductListPaginationProvider"
    );
  }
  return controllerValue;
};
